import React, { useState, useEffect } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';

interface PushNotificationPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}

const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({
  isOpen,
  onClose,
  onAccept,
  onDecline,
}) => {
  const { isSupported, subscribe, isLoading, isSubscribed } = usePushNotifications();
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Vérifier si on doit afficher le prompt
    if (isOpen && isSupported) {
      setShowPrompt(true);
    }
  }, [isOpen, isSupported]);

  const handleAccept = async () => {
    try {
      const success = await subscribe();
      if (success) {
        onAccept();
        setShowPrompt(false);
        onClose();
      } else {
        console.error('🔔 [PUSH PROMPT] Abonnement échoué depuis le prompt');
        alert('Impossible de s\'abonner aux notifications push. Vérifiez que votre navigateur autorise les notifications et que vous êtes connecté à Internet.');
      }
    } catch (error) {
      console.error('🔔 [PUSH PROMPT] Erreur lors de l\'abonnement aux notifications:', error);
      alert('Erreur technique lors de l\'abonnement aux notifications push. Veuillez réessayer plus tard.');
    }
  };

  const handleDecline = () => {
    onDecline();
    setShowPrompt(false);
    onClose();
  };

  // Si le prompt est ouvert et que les notifications sont supportées, essayer de s'abonner automatiquement
  useEffect(() => {
    if (isOpen && isSupported && !isLoading && !isSubscribed) {
      console.log('🔔 [PUSH PROMPT] Tentative d\'abonnement automatique depuis le prompt');
      const attemptAutoSubscribe = async () => {
        try {
          const success = await subscribe();
          if (success) {
            console.log('🔔 [PUSH PROMPT] Abonnement automatique réussi depuis le prompt');
            onAccept();
            setShowPrompt(false);
            onClose();
          } else {
            console.log('🔔 [PUSH PROMPT] Abonnement automatique échoué, affichage du prompt classique');
            // En cas d'échec, le prompt restera affiché pour une action manuelle
          }
        } catch (error) {
          console.log('🔔 [PUSH PROMPT] Erreur lors de l\'abonnement automatique:', error);
          // En cas d'erreur, le prompt restera affiché pour une action manuelle
        }
      };
      
      attemptAutoSubscribe();
    }
  }, [isOpen, isSupported, isLoading, isSubscribed, subscribe, onAccept, onClose]);

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Activer les notifications push
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Recevez des alertes en temps réel
            </p>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Activez les notifications push pour être informé immédiatement lorsque :
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Un courrier vous est assigné
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Une échéance approche
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Un commentaire est ajouté
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Même lorsque l'application est fermée
            </li>
          </ul>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleDecline}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50"
          >
            Plus tard
          </button>
          <button
            onClick={handleAccept}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Activation...
              </>
            ) : (
              'Activer les notifications'
            )}
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          Vous pourrez modifier ces paramètres à tout moment dans les paramètres de l'application
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;